/**
 * Deterministic recurrence expansion for service agreements (core-data-model Q4). The
 * schedule template expands to a series of `occurrence_key` dates that are a PURE FUNCTION
 * of the template — same input, same keys, in org-local wall dates. This is what makes
 * generation idempotent: a unique constraint on (agreement_id, occurrence_key) + INSERT …
 * ON CONFLICT DO NOTHING means re-running the cron is a structural no-op.
 */
export interface AgreementSchedule {
  freq: 'monthly' | 'yearly';
  /** monthly: every N months from the start date. */
  interval: number;
  /** yearly: which months (1–12), e.g. [4, 10] for spring + fall HVAC PM. */
  months?: number[];
  /** day of month (1–28 to stay valid in every month); defaults to the start date's day. */
  dayOfMonth?: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function key(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}
function parse(s: string): { y: number; m: number; d: number } {
  const [y, m, d] = s.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
}
function addMonths(p: { y: number; m: number; d: number }, n: number) {
  const total = p.y * 12 + (p.m - 1) + n;
  return { y: Math.floor(total / 12), m: (total % 12) + 1, d: p.d };
}

/**
 * Expand `schedule` to the occurrence_key dates (YYYY-MM-DD) in [from, to] that fall on or
 * after `startDate`. Deterministic and stable — the whole idempotency story rests on it.
 */
/**
 * The day of the month a schedule actually lands on. Clamped to 28 so every month has it —
 * a plan anchored on the 31st would silently skip February, which is the month a furnace
 * plan can least afford to miss.
 *
 * Exported because the UI has to be able to SAY this. A shop that types "first visit on the
 * 31st" and gets visits on the 28th is owed the reason on screen, and a second copy of the
 * clamp in the frontend is how the screen and the scheduler drift apart.
 */
export function scheduleAnchorDay(
  schedule: Pick<AgreementSchedule, 'dayOfMonth'>,
  startDate: string,
): number {
  return Math.min(28, schedule.dayOfMonth ?? parse(startDate).d);
}

export function expandSchedule(
  schedule: AgreementSchedule,
  startDate: string,
  from: string,
  to: string,
): string[] {
  const start = parse(startDate);
  const day = scheduleAnchorDay(schedule, startDate);
  const out: string[] = [];

  if (schedule.freq === 'monthly') {
    const interval = Math.max(1, Math.trunc(schedule.interval));
    let cur = { y: start.y, m: start.m, d: day };
    for (let i = 0; i < 1200; i++) {
      const k = key(cur.y, cur.m, cur.d);
      if (k > to) break;
      if (k >= from && k >= key(start.y, start.m, day)) out.push(k);
      cur = addMonths(cur, interval);
    }
  } else {
    const months = (schedule.months ?? [start.m]).slice().sort((a, b) => a - b);
    const fromY = parse(from).y;
    const toY = parse(to).y;
    const startKey = key(start.y, start.m, day);
    for (let y = Math.max(start.y, fromY); y <= toY; y++) {
      for (const m of months) {
        const k = key(y, m, day);
        if (k >= from && k <= to && k >= startKey) out.push(k);
      }
    }
  }
  return out.sort();
}
